import { config } from "@/app/config";
import { useAccessTokenClaims } from "@/shared/auth/useAccessTokenClaims";
import { useAuth0 } from "@auth0/auth0-react";
import { ALL_ROLES, type Role } from "@keystone/contracts/shared";
import { useMemo } from "react";

export interface CurrentUserView {
  sub: string;
  email: string | undefined;
  name: string | undefined;
  role: Role | undefined;
  permissions: string[];
}

export function useCurrentUser(): CurrentUserView | null {
  const { user, isAuthenticated } = useAuth0();
  const accessClaims = useAccessTokenClaims();

  return useMemo(() => {
    if (!isAuthenticated || !user) return null;

    const rolesRaw = accessClaims?.[config.rolesClaim];
    const permissionsRaw = accessClaims?.permissions ?? accessClaims?.[config.permissionsClaim];

    const roles = Array.isArray(rolesRaw)
      ? rolesRaw.filter((r): r is string => typeof r === "string")
      : [];
    const role = roles.find((r): r is Role => (ALL_ROLES as string[]).includes(r));
    const permissions = Array.isArray(permissionsRaw)
      ? permissionsRaw.filter((p): p is string => typeof p === "string")
      : [];

    return {
      sub: user.sub ?? "",
      email: user.email,
      name: user.name,
      role,
      permissions,
    };
  }, [user, isAuthenticated, accessClaims]);
}
